import React from 'react'
import { makeStyles } from '@material-ui/styles'
import * as R from 'ramda'
import { Tooltip, Typography } from '@material-ui/core'
import PropTypes from 'prop-types'
import clsx from 'clsx'

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: 4,
    display: 'flex',
    height: 30,
    '& :first-child': {
      borderTopLeftRadius: 8,
      borderBottomLeftRadius: 8,
    },
    '& :last-child': {
      borderTopRightRadius: 8,
      borderBottomRightRadius: 8,
    },
  },
  item: {
    minWidth: 8,
  },
  darkGreyText: {
    color: theme.palette.darkGrey.main,
  },
  circleStyle: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginRight: '0.7rem',
  },
  descriptionContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: '1rem 0',
  },
}))

const mapIndexed = R.addIndex(R.map)

const StackedBarChart = ({
  title,
  description,
  data,
  headerStyle,
  descriptionStyle,
  emptyMessage,
  catchedError,
  optional,
  titleIcon = null,
  titleDescription = null,
}) => {
  const classes = useStyles()
  let core = ({ value, backgroundColor, title, index = title }) => {
    return (
      <Tooltip
        disableFocusListener={!title}
        disableHoverListener={!title}
        placement="top"
        key={`bar-${index}`}
        title={<TooltipBody title={title} value={value} />}
      >
        <span
          className={classes.item}
          style={{ backgroundColor, flex: value }}
        />
      </Tooltip>
    )
  }
  let viewData = R.compose(
    mapIndexed(({ backgroundColor, title, value }, index) => {
      return core({ value, backgroundColor, title, index })
    }),
    R.filter(({ value, isSliver = false }) => value > 0 || isSliver),
    R.defaultTo([]),
  )(data)

  if (R.isEmpty(viewData) || catchedError) {
    viewData = core({
      value: '1 0 100%',
      backgroundColor: 'grey',
      title: emptyMessage || catchedError,
      index: 'bar is empty',
    })
  }

  return (
    <>
      <div className="flex justify-between">
        <div className="flex">
          {titleIcon}
          <Typography
            variant="body1"
            className={clsx(classes.darkGreyText, 'font-semibold', headerStyle)}
          >
            {title}
          </Typography>
          <Typography className="hidden sm:block mx-5">
            {titleDescription}
          </Typography>
        </div>
        {optional && (
          <Typography
            variant="body1"
            className={clsx(classes.darkGreyText, 'font-semibold', headerStyle)}
          >
            {optional}
          </Typography>
        )}
      </div>
      {description && (
        <Typography
          variant="caption"
          className={clsx(classes.darkGreyText, descriptionStyle)}
        >
          <div>
            {description.length
              ? description.map(
                  (item, index) =>
                    item.isShown && (
                      <div
                        className={classes.descriptionContainer}
                        key={`description-index-${index}`}
                      >
                        <span
                          className={classes.circleStyle}
                          style={{ backgroundColor: item.color }}
                        />
                        <p>{item.text}</p>
                      </div>
                    ),
                )
              : null}
          </div>
        </Typography>
      )}
      <div className={classes.root}>{viewData}</div>
    </>
  )
}

const TooltipBody = ({ title }) => (
  <div className="flex flex-col items-center content-center">
    <Typography variant="caption" className="text-center">
      {title}
    </Typography>
  </div>
)

StackedBarChart.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      isShown: PropTypes.bool,
      text: PropTypes.any,
    }),
  ),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.any,
      backgroundColor: PropTypes.string,
      value: PropTypes.number,
      isSliver: PropTypes.bool,
    }),
  ).isRequired,
}

export default StackedBarChart
